// ============================================================================
// AdherenceSection — completion rate as an animated ring + completed/planned
// counts. Renders only when there's at least one planned-or-completed session.
// ============================================================================
import { useTranslation } from "react-i18next";
import { ProgressArc } from "../charts/ProgressArc";
import { ReportSection } from "./ReportSection";
import type { TypedClientReport } from "../types";
import { fmtNum } from "../format";

export function AdherenceSection({
  report,
  index,
}: {
  report: TypedClientReport;
  index: number;
}) {
  const { t, i18n } = useTranslation("clientReport");
  const locale = i18n.language;
  const { adherence } = report;
  const pct = adherence.completion_pct ?? 0;
  const total = adherence.completed + adherence.planned;

  return (
    <ReportSection
      id="report-adherence"
      index={index}
      kicker={t("kicker")}
      title={t("adherence.title")}
      subtitle={t("adherence.subtitle")}
      hasData={adherence.ok}
    >
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
        <ProgressArc
          pct={pct}
          accent="coral"
          ariaLabel={`${t("adherence.completion")} ${Math.round(pct)}%`}
        >
          <span className="nums font-display text-3xl font-extrabold leading-none tracking-tightest text-body">
            {fmtNum(Math.round(pct), 0, locale)}
            <span className="text-lg">%</span>
          </span>
          <span className="mt-0.5 text-[10px] font-bold uppercase tracking-kicker text-body-faint">
            {t("adherence.completion")}
          </span>
        </ProgressArc>

        <div className="grid w-full grid-cols-2 gap-4 sm:max-w-xs">
          <div className="rounded-lg bg-lime-soft p-4 text-center">
            <p className="nums font-display text-3xl font-extrabold tracking-tightest text-lime-600">
              {fmtNum(adherence.completed, 0, locale)}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-kicker text-body-soft">
              {t("adherence.completed")}
            </p>
          </div>
          <div className="rounded-lg bg-ocean-soft p-4 text-center">
            <p className="nums font-display text-3xl font-extrabold tracking-tightest text-ocean-600">
              {fmtNum(adherence.planned, 0, locale)}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-kicker text-body-soft">
              {t("adherence.planned")}
            </p>
          </div>
          <p className="col-span-2 text-center text-sm text-body-soft">
            {fmtNum(adherence.completed, 0, locale)} {t("adherence.sessions")}{" "}
            {t("adherence.ofPlanned", { total })}
          </p>
        </div>
      </div>
    </ReportSection>
  );
}
